import React, { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const MATCH_COLORS = {
  match:      'text-green-400',
  full_match: 'text-green-400',
  partial:    'text-yellow-400',
  mismatch:   'text-red-400',
  timeout:    'text-slate-300',  // cloud/no-exec: neutral, readable, not failure-red
  skipped:    'text-gray-300',
  pending:    'text-gray-400',
};

const STATUS_DOT = {
  completed: 'bg-green-500',
  failed:    'bg-red-500',
  running:   'bg-yellow-400 animate-pulse',
  pending:   'bg-gray-500',
};

function matchBadge(status) {
  const cls = MATCH_COLORS[status] || 'text-gray-400';
  return <span className={`font-mono text-xs font-semibold ${cls}`}>{status || '—'}</span>;
}

function policyList(arr) {
  if (!arr || arr.length === 0) return <span className="text-gray-400">—</span>;
  return arr.map(p => (
    <span key={p} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-gray-600 text-gray-100 text-xs font-mono">
      P{p.replace(/^P/i, '')}
    </span>
  ));
}

// ---------------------------------------------------------------------------
// Scenario card
// ---------------------------------------------------------------------------
function ScenarioCard({ scenario, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(scenario)}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        selected
          ? 'border-indigo-400 bg-indigo-900/60 ring-1 ring-indigo-500/40'
          : 'border-gray-600 bg-gray-800 hover:border-gray-400 hover:bg-gray-750'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{scenario.name}</p>
          <p className={`text-xs mt-0.5 line-clamp-2 ${selected ? 'text-indigo-200' : 'text-gray-300'}`}>
            {scenario.description}
          </p>
        </div>
        <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded font-mono ${
          selected ? 'bg-indigo-700/60 text-indigo-100' : 'bg-gray-700 text-gray-300'
        }`}>
          {scenario.step_count}s
        </span>
      </div>
      {scenario.expected_policies?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {scenario.expected_policies.map(p => (
            <span key={p} className={`text-xs px-1 py-0.5 rounded font-mono ${
              selected ? 'bg-indigo-700/50 text-indigo-100' : 'bg-gray-700 text-indigo-300'
            }`}>
              P{p.replace(/^P/i, '')}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Step progress row
// ---------------------------------------------------------------------------
function StepRow({ step, index }) {
  return (
    <tr className="border-t border-gray-700/50 hover:bg-gray-700/20">
      <td className="py-2 px-3 text-xs text-gray-200 font-mono">{step.step_id || `s${index + 1}`}</td>
      <td className="py-2 px-3">{matchBadge(step.match_status || step.status)}</td>
      <td className="py-2 px-3">{policyList(step.expected_policies)}</td>
      <td className="py-2 px-3">{policyList(step.observed_policies)}</td>
      <td className="py-2 px-3 text-xs text-gray-300 max-w-xs truncate">{step.notes || ''}</td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// After-action panel
// ---------------------------------------------------------------------------
function AfterAction({ summary }) {
  if (!summary) return null;
  const matched = summary.steps_matched || 0;
  const partial = summary.steps_partial || 0;
  const total   = summary.steps_total   || 0;
  const pct = total > 0 ? Math.round(((matched + partial * 0.5) / total) * 100) : 0;

  return (
    <div className={`mt-4 p-4 rounded-lg border ${summary.success ? 'border-green-700 bg-green-900/20' : 'border-red-700/50 bg-red-900/10'}`}>
      <h3 className="text-sm font-semibold text-white mb-3">After-Action Summary</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center mb-3">
        {[
          { label: 'Steps',   value: total },
          { label: 'Matched', value: matched, cls: 'text-green-400' },
          { label: 'Partial', value: partial, cls: 'text-yellow-400' },
          { label: 'Score',   value: `${pct}%`, cls: pct >= 70 ? 'text-green-400' : 'text-red-400' },
        ].map(({ label, value, cls }) => (
          <div key={label} className="bg-gray-800 rounded p-2">
            <p className={`text-xl font-bold ${cls || 'text-white'}`}>{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-6 text-xs text-gray-400 flex-wrap">
        <span>Expected: {policyList(summary.expected_policies)}</span>
        <span>Observed: {policyList(summary.observed_policies)}</span>
      </div>
      {summary.note && (
        <p className="mt-2 text-xs text-blue-400 opacity-80">{summary.note}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdversarySimulation() {
  const [scenarios,       setScenarios]       = useState([]);
  const [selected,        setSelected]        = useState(null);
  const [dryRun,          setDryRun]          = useState(true);
  const [randomize,       setRandomize]       = useState(false);
  const [activeRun,       setActiveRun]       = useState(null);
  const [recentRuns,      setRecentRuns]      = useState([]);
  const [launching,       setLaunching]       = useState(false);
  const [error,           setError]           = useState(null);
  const [validation,      setValidation]      = useState(null);
  const [afterAction,     setAfterAction]     = useState(null);
  const [runnerAvailable, setRunnerAvailable] = useState(true);
  const pollRef = useRef(null);

  // Load scenario list once
  useEffect(() => {
    fetch(`${API}/api/scenarios`)
      .then(r => r.json())
      .then(d => {
        setScenarios(d.scenarios || []);
        if (d.scenarios?.length) setSelected(d.scenarios[0]);
        setRunnerAvailable(d.runner_available !== false);
      })
      .catch(e => setError(`Failed to load scenarios: ${e.message}`));

    fetch(`${API}/api/scenario-runs?limit=10`)
      .then(r => r.json())
      .then(d => setRecentRuns(d.runs || []))
      .catch(() => {});
  }, []);

  // Poll active run
  useEffect(() => {
    if (!activeRun?.run_id) {
      clearInterval(pollRef.current);
      return;
    }
    const runId = activeRun.run_id;
    const done = ['completed', 'failed'];

    const poll = async () => {
      try {
        const [stateR, valR, aarR] = await Promise.all([
          fetch(`${API}/api/scenario-runs/${runId}`),
          fetch(`${API}/api/scenario-runs/${runId}/validation`),
          fetch(`${API}/api/scenario-runs/${runId}/after-action`),
        ]);
        const state = await stateR.json();
        const val   = await valR.json();
        const aar   = await aarR.json();
        setActiveRun(state);
        setValidation(val);
        if (aar.after_action_summary) setAfterAction(aar.after_action_summary);

        if (done.includes(state.status)) {
          clearInterval(pollRef.current);
          // One final poll after 3s to catch any step results still in-flight
          // when the run status flipped to completed (e.g. c6 observer finishes
          // milliseconds after the runner marks the run done).
          setTimeout(() => poll(), 3000);
          // Refresh recent runs list
          fetch(`${API}/api/scenario-runs?limit=10`)
            .then(r => r.json())
            .then(d => setRecentRuns(d.runs || []))
            .catch(() => {});
        }
      } catch (e) {
        // Non-fatal during poll
      }
    };

    clearInterval(pollRef.current);
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [activeRun?.run_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLaunch = async () => {
    if (!selected) return;
    setLaunching(true);
    setError(null);
    setValidation(null);
    setAfterAction(null);
    try {
      const r = await fetch(`${API}/api/scenarios/${selected.scenario_id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ randomize_order: randomize, dry_run_enforcement: dryRun }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || r.statusText);
      }
      const data = await r.json();
      setActiveRun({ run_id: data.run_id, scenario_id: data.scenario_id, status: 'pending', step_results: [] });
    } catch (e) {
      setError(`Launch failed: ${e.message}`);
    } finally {
      setLaunching(false);
    }
  };

  const handleLoadRun = async (run) => {
    setActiveRun(run);
    setValidation(null);
    setAfterAction(null);
    try {
      const [valR, aarR] = await Promise.all([
        fetch(`${API}/api/scenario-runs/${run.run_id}/validation`),
        fetch(`${API}/api/scenario-runs/${run.run_id}/after-action`),
      ]);
      setValidation(await valR.json());
      const aar = await aarR.json();
      if (aar.after_action_summary) setAfterAction(aar.after_action_summary);
    } catch (_) {}
  };

  const isRunning   = activeRun && ['pending', 'running'].includes(activeRun.status);
  const isCloudMode = activeRun?.cloud_mode || afterAction?.cloud_mode;
  const steps       = (validation?.steps || activeRun?.step_results || []);

  return (
    <div className="flex flex-col gap-4 p-4 h-full text-white">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-white">Live Adversary Simulation</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {runnerAvailable
              ? 'Real packet injection · Real policy detections · Real backend events'
              : 'Cloud preview · Live execution requires Alienware + Pi substation'}
          </p>
        </div>
        {activeRun && (
          <div className="flex items-center gap-2">
            {isCloudMode && (
              <span className="text-xs px-2 py-0.5 rounded bg-blue-800/60 border border-blue-500 text-blue-100 font-medium">
                cloud preview
              </span>
            )}
            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[activeRun.status] || 'bg-gray-600'}`} />
            <span className="text-xs text-gray-400 font-mono">
              {activeRun.status.toUpperCase()} · {activeRun.run_id?.slice(0, 8)}
            </span>
          </div>
        )}
      </div>

      {!runnerAvailable && (
        <div className="px-3 py-2 rounded bg-blue-900/40 border border-blue-500 text-blue-100 text-xs">
          <span className="font-semibold text-white">Cloud preview mode</span> — scenario definitions and expected detection flow are shown.
          Live packet injection runs from the Alienware via <code className="font-mono text-blue-200">./tests/run_substation_demo.sh</code>
        </div>
      )}

      {error && (
        <div className="px-3 py-2 rounded bg-red-900/40 border border-red-700 text-red-300 text-sm">{error}</div>
      )}

      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        {/* Left column — Scenario picker */}
        <div className="w-72 shrink-0 flex flex-col gap-2 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-0.5">Scenarios</p>
          {scenarios.length === 0 && (
            <p className="text-xs text-gray-600 px-1">Loading…</p>
          )}
          {scenarios.map(s => (
            <ScenarioCard
              key={s.scenario_id}
              scenario={s}
              selected={selected?.scenario_id === s.scenario_id}
              onSelect={setSelected}
            />
          ))}

          {/* Controls */}
          <div className="mt-2 p-3 rounded-lg bg-gray-800 border border-gray-700 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={e => setDryRun(e.target.checked)}
                className="rounded"
              />
              <span className="text-xs text-gray-300">Dry run (no packets)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={randomize}
                onChange={e => setRandomize(e.target.checked)}
                className="rounded"
              />
              <span className="text-xs text-gray-300">Randomize step order</span>
            </label>
            <button
              onClick={handleLaunch}
              disabled={!selected || launching || isRunning}
              className="w-full py-2 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
            >
              {launching ? 'Launching…'
                : isRunning ? 'Running…'
                : runnerAvailable ? 'Launch Scenario'
                : 'Preview Scenario'}
            </button>
          </div>

          {/* Recent runs */}
          {recentRuns.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 px-0.5">Recent Runs</p>
              <div className="space-y-1">
                {recentRuns.slice(0, 6).map(r => (
                  <button
                    key={r.run_id}
                    onClick={() => handleLoadRun(r)}
                    className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors ${
                      activeRun?.run_id === r.run_id
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${STATUS_DOT[r.status] || 'bg-gray-600'}`} />
                    <span className="font-mono">{r.run_id?.slice(0, 8)}</span>
                    <span className="ml-1 text-gray-400">{r.scenario_id?.replace('substation-', '')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column — Run detail */}
        <div className="flex-1 min-w-0 overflow-y-auto space-y-4">
          {!activeRun && (
            <div className="flex flex-col items-center justify-center h-40 text-gray-600">
              <svg className="w-10 h-10 mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <p className="text-sm">Select a scenario and launch</p>
            </div>
          )}

          {activeRun && (
            <>
              {/* Run header */}
              <div className="p-3 rounded-lg bg-gray-800 border border-gray-700">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT[activeRun.status] || 'bg-gray-600'}`} />
                    <span className="text-sm font-semibold capitalize">{activeRun.status}</span>
                  </div>
                  <span className="text-gray-600">·</span>
                  <span className="text-xs text-gray-400 font-mono">{activeRun.run_id}</span>
                  <span className="text-gray-600">·</span>
                  <span className="text-xs text-gray-400">{activeRun.scenario_id}</span>
                </div>
              </div>

              {/* Step table */}
              {steps.length > 0 && (
                <div className="rounded-lg border border-gray-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/80">
                      <tr>
                        {['Step', 'Status', 'Expected', 'Observed', 'Notes'].map(h => (
                          <th key={h} className="py-2 px-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/30">
                      {steps.map((step, i) => <StepRow key={step.step_id || i} step={step} index={i} />)}
                    </tbody>
                  </table>
                </div>
              )}

              {/* No steps yet */}
              {steps.length === 0 && isRunning && (
                <div className="flex items-center gap-2 text-xs text-gray-500 px-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                  Waiting for first step…
                </div>
              )}

              {/* After-action */}
              <AfterAction summary={afterAction} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
